import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  fetchAuthSession,
  signInWithRedirect,
  signOut as amplifySignOut,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import type { AxiosError } from "axios";

import { apiClient } from "../api/client";
import type { MeResponse } from "../types";
import {
  AuthContext,
  type AuthContextValue,
  type AuthUser,
  type ProvisioningError,
} from "./AuthContext";

interface AuthProviderProps {
  children: ReactNode;
}

function toUser(me: MeResponse): AuthUser {
  return {
    userId: me.user_id,
    email: me.email,
    companyId: me.company_id,
    companyName: me.company_name,
    role: me.role,
    firstName: me.first_name,
    lastName: me.last_name,
    avatarUrl: me.avatar_url,
  };
}

/** ms before a 503-stuck provisioning state flips to provisioningTimedOut. */
const PROVISIONING_TIMEOUT_MS = 15_000;

/**
 * Type-guard: unwrap the structured 422 body the backend emits when
 * Cognito claims are malformed. FastAPI puts our dict under ``detail``;
 * if someone hits an unrelated 422 (Pydantic body validation, etc.)
 * the shape won't match and we silently fall through to the generic
 * "provisioning timed out" UI instead of crashing on missing fields.
 */
function extractProvisioningError(body: unknown): ProvisioningError | null {
  if (typeof body !== "object" || body === null) return null;
  const detailField = (body as { detail?: unknown }).detail;
  if (typeof detailField !== "object" || detailField === null) return null;
  const d = detailField as Record<string, unknown>;
  if (
    typeof d.detail === "string" &&
    typeof d.error_code === "string" &&
    typeof d.field === "string" &&
    typeof d.remediation === "string"
  ) {
    return {
      detail: d.detail,
      error_code: d.error_code,
      field: d.field,
      remediation: d.remediation,
    };
  }
  return null;
}

/**
 * Auth state machine:
 *
 *   isLoading=true            → no decision yet; render a neutral state.
 *   user=AuthUser             → fully authed; render the app.
 *   user=null                 → not authed; render the splash (no error chrome).
 *   provisioning=true         → tokens exist but /api/auth/me returned 503
 *                               (post-confirmation Lambda still creating the
 *                               DB rows). UI shows "Setting up your account…"
 *                               with an always-visible Sign Out escape.
 *   provisioningTimedOut=true → 15s have elapsed in the provisioning state
 *                               without progress. UI shows a hard error card
 *                               with a Sign Out button so the user is never
 *                               trapped indefinitely.
 *
 * Raw axios error strings NEVER reach the UI. /api/auth/me failures are
 * classified into either "not signed in" (silent) or "provisioning" (soft
 * status). Everything else is logged to console and treated as "not signed in".
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [claims, setClaims] = useState<Record<string, unknown> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [provisioning, setProvisioning] = useState(false);
  const [provisioningTimedOut, setProvisioningTimedOut] = useState(false);
  const [provisioningError, setProvisioningError] =
    useState<ProvisioningError | null>(null);

  const mountedRef = useRef(true);
  const provisioningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear any pending provisioning timer + reset its derived flag.
  const clearProvisioningTimer = useCallback(() => {
    if (provisioningTimerRef.current !== null) {
      clearTimeout(provisioningTimerRef.current);
      provisioningTimerRef.current = null;
    }
  }, []);

  const loadSession = useCallback(async () => {
    setIsLoading(true);
    setProvisioning(false);
    setProvisioningTimedOut(false);
    setProvisioningError(null);
    clearProvisioningTimer();

    let session;
    try {
      session = await fetchAuthSession();
    } catch (err) {
      // fetchAuthSession should never throw under normal conditions; if it
      // does (e.g. Cognito config bad), treat as unauthenticated and surface
      // only in the console for debugging. The splash is the right UX.
      console.debug("fetchAuthSession threw — treating as signed-out", err);
      if (mountedRef.current) {
        setUser(null);
        setClaims(null);
        setIsLoading(false);
      }
      return;
    }

    const accessToken = session.tokens?.accessToken;
    if (!accessToken) {
      if (mountedRef.current) {
        setUser(null);
        setClaims(null);
        setIsLoading(false);
      }
      return;
    }

    if (mountedRef.current) {
      setClaims(accessToken.payload as Record<string, unknown>);
    }

    try {
      const resp = await apiClient.get<MeResponse>("/api/auth/me");
      if (mountedRef.current) {
        setUser(toUser(resp.data));
        setProvisioning(false);
        setProvisioningTimedOut(false);
        setProvisioningError(null);
        clearProvisioningTimer();
        setIsLoading(false);
      }
    } catch (err) {
      const axiosErr = err as AxiosError;
      const status = axiosErr.response?.status;
      if (status === 422) {
        // Backend says: Cognito user has malformed custom attributes
        // (e.g. company_id="Sunwave Digital" instead of co-sunwave-digital).
        // This is NOT transient — no amount of retrying will fix it. Skip
        // the 15-second timer and go straight to the error UI with the
        // exact remediation message the backend computed.
        const structured = extractProvisioningError(axiosErr.response?.data);
        if (structured) {
          console.warn(
            "/api/auth/me 422 — malformed Cognito claims: %s",
            structured.error_code,
          );
          if (mountedRef.current) {
            setUser(null);
            setProvisioning(true);
            setProvisioningTimedOut(true);
            setProvisioningError(structured);
            clearProvisioningTimer();
            setIsLoading(false);
          }
          return;
        }
        // Unstructured 422 (Pydantic body-validation, etc.) — fall
        // through to the generic "treat as signed-out" path below.
      }
      if (status === 503) {
        // Backend says: tokens valid, but the post-confirmation Lambda
        // hasn't finished writing the DB rows. Soft "provisioning" state.
        // Start a 15s safety timer — if the state hasn't cleared by then
        // we surface a hard error so the user isn't trapped.
        console.debug("/api/auth/me 503 — provisioning in progress");
        if (mountedRef.current) {
          setUser(null);
          setProvisioning(true);
          setProvisioningError(null);
          setIsLoading(false);
          clearProvisioningTimer();
          provisioningTimerRef.current = setTimeout(() => {
            if (mountedRef.current) {
              console.warn(
                "Provisioning timed out after %d ms — surfacing escape UI",
                PROVISIONING_TIMEOUT_MS,
              );
              setProvisioningTimedOut(true);
            }
          }, PROVISIONING_TIMEOUT_MS);
        }
        return;
      }
      if (status === 401) {
        // Tokens are present but invalid/expired. Sign out and clear state.
        // The 401 interceptor in api/client.ts already kicks off signOut();
        // we also clear local React state here so we don't render a stale
        // half-authed UI.
        console.debug("/api/auth/me 401 — signing out");
        if (mountedRef.current) {
          setUser(null);
          setClaims(null);
          setProvisioning(false);
          setProvisioningTimedOut(false);
          setProvisioningError(null);
          clearProvisioningTimer();
          setIsLoading(false);
        }
        return;
      }
      // Network error, 5xx other than 503, etc. — log and treat as
      // signed-out. Never surface raw axios strings to users.
      console.warn("/api/auth/me failed", err);
      if (mountedRef.current) {
        setUser(null);
        setProvisioning(false);
        setProvisioningTimedOut(false);
        setProvisioningError(null);
        clearProvisioningTimer();
        setIsLoading(false);
      }
    }
  }, [clearProvisioningTimer]);

  useEffect(() => {
    mountedRef.current = true;
    void loadSession();

    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
        case "signInWithRedirect":
        case "tokenRefresh":
          void loadSession();
          break;
        case "signedOut":
          setUser(null);
          setClaims(null);
          setProvisioning(false);
          setProvisioningTimedOut(false);
          setProvisioningError(null);
          clearProvisioningTimer();
          setIsLoading(false);
          break;
        case "signInWithRedirect_failure":
        case "tokenRefresh_failure":
          console.warn("Amplify auth Hub failure event", payload.event);
          setUser(null);
          setClaims(null);
          setProvisioning(false);
          setProvisioningTimedOut(false);
          setProvisioningError(null);
          clearProvisioningTimer();
          setIsLoading(false);
          break;
        default:
          break;
      }
    });

    return () => {
      mountedRef.current = false;
      clearProvisioningTimer();
      unsubscribe();
    };
  }, [loadSession, clearProvisioningTimer]);

  const signIn = useCallback(async () => {
    try {
      await signInWithRedirect();
    } catch (err) {
      // Amplify v6 throws ``UserAlreadyAuthenticatedException`` if there is
      // already a valid Amplify session in storage. The right recovery is
      // to clear that stale session and start fresh. We do NOT want to
      // bounce the user through the Hosted UI just to land on an error.
      const name = (err as { name?: string })?.name;
      if (name === "UserAlreadyAuthenticatedException") {
        console.debug(
          "signInWithRedirect found existing session — signing out and retrying",
        );
        try {
          await amplifySignOut();
        } catch (signOutErr) {
          console.debug("signOut during recovery failed", signOutErr);
        }
        // Now retry. If this still throws, let the Hub failure handler take it.
        try {
          await signInWithRedirect();
        } catch (retryErr) {
          console.warn("signInWithRedirect retry failed", retryErr);
        }
        return;
      }
      console.warn("signInWithRedirect failed", err);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await amplifySignOut();
    } catch (err) {
      console.debug("amplifySignOut threw", err);
    }
    setUser(null);
    setClaims(null);
    setProvisioning(false);
    setProvisioningTimedOut(false);
    setProvisioningError(null);
    clearProvisioningTimer();
    setIsLoading(false);
  }, [clearProvisioningTimer]);

  const value: AuthContextValue = useMemo(
    () => ({
      user,
      claims,
      isAuthenticated: user !== null,
      isLoading,
      provisioning,
      provisioningTimedOut,
      provisioningError,
      // ``error`` is preserved on the context for downstream code that
      // might check it, but AuthProvider no longer ever sets it to a raw
      // axios message — see classification logic in ``loadSession``.
      error: null,
      signIn,
      signOut,
      refresh: loadSession,
    }),
    [
      user,
      claims,
      isLoading,
      provisioning,
      provisioningTimedOut,
      provisioningError,
      signIn,
      signOut,
      loadSession,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
